/**
 * EltJobTable Component
 *
 * Table displaying ELT jobs with actions, row selection, and copy support.
 */

import React from 'react';
import { Button } from '../../../components/ui/Button';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import type { EltJob } from '../../../types/elt-job.types';
import { EyeIcon, PencilIcon, TrashIcon, PlayIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';

interface EltJobTableProps {
  eltJobs: EltJob[];
  isLoading: boolean;
  selectedJobIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  onView: (job: EltJob) => void;
  onEdit: (job: EltJob) => void;
  onDelete: (job: EltJob) => void;
  onTrigger: (job: EltJob) => void;
  onCopy: (job: EltJob) => void;
}

export const EltJobTable: React.FC<EltJobTableProps> = ({
  eltJobs,
  isLoading,
  selectedJobIds,
  onSelectionChange,
  onView,
  onEdit,
  onDelete,
  onTrigger,
  onCopy,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={7} />;
  }

  if (eltJobs.length === 0) {
    return (
      <EmptyState
        title="No ELT jobs found"
        description="No ELT jobs found."
      />
    );
  }

  const formatCron = (cron?: string, timezone?: string) => {
    if (!cron) return 'Manual only';
    const tz = timezone || 'UTC';
    return `${cron} (${tz})`;
  };

  const allSelected = eltJobs.length > 0 && eltJobs.every((job) => selectedJobIds.has(job.eltJobId));
  const someSelected = eltJobs.some((job) => selectedJobIds.has(job.eltJobId)) && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(eltJobs.map((job) => job.eltJobId)));
    }
  };

  const handleSelectRow = (jobId: string) => {
    const newSelection = new Set(selectedJobIds);
    if (newSelection.has(jobId)) {
      newSelection.delete(jobId);
    } else {
      newSelection.add(jobId);
    }
    onSelectionChange(newSelection);
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Job
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Schedule
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Output
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Queries
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Active
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {eltJobs.map((job) => (
            <tr key={job.eltJobId} className="hover:bg-gray-50">
              <td className="px-4 py-4">
                <input
                  type="checkbox"
                  checked={selectedJobIds.has(job.eltJobId)}
                  onChange={() => handleSelectRow(job.eltJobId)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {job.eltJobName}
                  </div>
                  <div className="text-sm text-gray-500 font-mono">
                    {job.eltJobCode}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-gray-500 font-mono">
                  {formatCron(job.cronExpression, job.timezone)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {job.outputFormat.toUpperCase()}
                  {job.compression !== 'none' && (
                    <span className="ml-1 text-gray-500">
                      ({job.compression})
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {job.queries.filter((q) => q.isActive).length} / {job.queries.length}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    job.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {job.isActive ? 'Yes' : 'No'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onView(job)}
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onTrigger(job)}
                    title="Run Now"
                    disabled={!job.isActive}
                  >
                    <PlayIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onCopy(job)}
                    title="Copy Job"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onEdit(job)}
                    title="Edit"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onDelete(job)}
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
